import React, { FC } from 'react';
import { Badge } from '../ui';
import { createBillingPortalSession } from '../../lib/billingPortal';

type SeniorStatus = 'online' | 'offline' | 'attention';

interface SeniorCardProps {
  name: string;
  deviceName: string;
  profileType: string;
  status: SeniorStatus;
  battery?: number;
  lastSeen?: string;
  avatar?: string;
  activationCode?: string;
  onViewActivity?: () => void;
  onEditConfig?: () => void;
  onEditContacts?: () => void;
  onEditSenior?: () => void;
  onDelete?: () => void;
  onViewCode?: () => void;
  onLocate?: () => void;
  hasLocation?: boolean;
  /** REMOVED with the safe-zone button — geofencing is CUT from v1. No caller
   *  ever passed it. Restore alongside the button in Wave 1.5. */
  onSubscribe?: () => void;
  subscriptionStatus?: 'trial' | 'active' | 'cancelled' | 'expired';
  trialEndsAt?: string;
  hasPayment?: boolean;
  stripeCustomerId?: string;
  /** Compact mode for mobile list view */
  compact?: boolean;
}

export const SeniorCard: FC<SeniorCardProps> = ({
  name,
  deviceName,
  profileType,
  status,
  battery,
  lastSeen,
  avatar,
  activationCode,
  onViewActivity,
  onEditConfig,
  onEditContacts,
  onEditSenior,
  onDelete,
  onViewCode,
  onLocate,
  hasLocation,
  onSubscribe,
  subscriptionStatus,
  trialEndsAt,
  hasPayment,
  stripeCustomerId,
  compact = false,
}) => {

  const handleManageBilling = async () => {
    // Shared client — see lib/billingPortal.ts. This was the THIRD copy of the
    // portal call, and like the other two it posted a customerId to what used
    // to be an unauthenticated endpoint. The server no longer accepts one.
    const result = await createBillingPortalSession();
    if (result.ok) {
      window.location.href = result.url;
      return;
    }
    // ManageSeniors is B2B-gated and unreachable today, so there is no error UI
    // here to hang a message on. Logging still beats the previous silent
    // `return`; whoever re-enables B2B should surface this properly.
    console.error('[SeniorCard] Billing portal failed:', result);
  };

  const formatTrialEndDate = () => {
    if (!trialEndsAt) return '';
    return new Date(trialEndsAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getTrialDaysLeft = () => {
    if (!trialEndsAt) return null;
    const now = new Date();
    const end = new Date(trialEndsAt);
    const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  const trialDaysLeft = subscriptionStatus === 'trial' ? getTrialDaysLeft() : null;
  const getProfileDisplayName = (type: string) => {
    const names: Record<string, string> = {
      cognitive: 'Memory & Confusion',
      physical: 'Physical & Mobility',
      visual: 'Vision',
      custom: 'Custom',
    };
    return names[type] || 'Standard';
  };

  const getProfileIcon = (type: string) => {
    const icons: Record<string, string> = {
      cognitive: 'psychology',
      physical: 'accessible',
      visual: 'visibility',
      custom: 'tune',
    };
    return icons[type] || 'person';
  };

  const isAttention = status === 'attention';
  const isOffline = status === 'offline';
  const isOnline = status === 'online';

  const batteryColor = (battery ?? 100) <= 15 ? 'text-red-500' : (battery ?? 100) <= 30 ? 'text-amber-500' : 'text-emerald-500';
  const batteryIcon = (battery ?? 100) <= 15 ? 'battery_alert' : (battery ?? 100) <= 30 ? 'battery_3_bar' : 'battery_full';

  // Compact mobile-friendly layout
  if (compact) {
    return (
      <div
        className={`flex items-center gap-3 rounded-xl p-3 transition-all ${
          isAttention
            ? 'border-2 border-amber-200 bg-amber-50/50'
            : 'border border-brand-border bg-white'
        }`}
      >
        {/* Avatar */}
        <div className={`relative w-12 h-12 rounded-full shrink-0 flex items-center justify-center ${
          isOnline ? 'bg-emerald-100' : isAttention ? 'bg-amber-100' : 'bg-brand-bg-warm'
        }`}>
          {avatar ? (
            <img src={avatar} alt={name} className="w-full h-full rounded-full object-cover" />
          ) : (
            <span className={`material-symbols-outlined text-xl ${
              isOnline ? 'text-emerald-600' : isAttention ? 'text-amber-600' : 'text-brand-text-muted'
            }`}>person</span>
          )}
          <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${
            isOnline ? 'bg-emerald-500' : isAttention ? 'bg-amber-500' : 'bg-gray-400'
          }`} />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h4 className="text-base font-semibold text-brand-text truncate">{name}</h4>
          <p className="text-sm text-brand-text-muted truncate">{deviceName}</p>
        </div>

        {/* Status/Battery */}
        <div className="shrink-0 text-right">
          {isAttention && battery !== undefined ? (
            <div className="flex items-center gap-1 text-amber-600">
              <span className="material-symbols-outlined text-sm">battery_alert</span>
              <span className="text-sm font-semibold">{battery}%</span>
            </div>
          ) : isOffline ? (
            <span className="text-sm text-brand-text-muted">{lastSeen}</span>
          ) : (
            <span className="text-sm text-emerald-600 font-medium">Online</span>
          )}
        </div>

        {/* Actions */}
        <button
          onClick={onEditConfig}
          className="shrink-0 p-2.5 rounded-lg bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 transition-colors"
        >
          <span className="material-symbols-outlined text-lg">edit</span>
        </button>
        {onDelete && (
          <button
            onClick={onDelete}
            className="shrink-0 p-2.5 rounded-lg text-brand-text-muted hover:bg-red-50 hover:text-red-500 transition-colors"
            aria-label="Delete senior"
          >
            <span className="material-symbols-outlined text-lg">delete</span>
          </button>
        )}
      </div>
    );
  }

  // Full card layout
  return (
    <div
      className="flex flex-col rounded-2xl transition-all relative group overflow-hidden bg-white shadow-md hover:shadow-xl"
    >
      <div className="p-4 sm:p-5 flex flex-col">
        {/* Top Row: Avatar + Name + Status */}
        <div className="flex items-start gap-3 sm:gap-4 mb-5">
          {/* Avatar with status ring */}
          <div className="relative shrink-0">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
              isOnline
                ? 'bg-emerald-50'
                : isAttention
                ? 'bg-amber-50'
                : 'bg-gray-100'
            }`}>
              {avatar ? (
                <img src={avatar} alt={name} className="w-full h-full rounded-2xl object-cover" />
              ) : (
                <span className={`material-symbols-outlined text-2xl ${
                  isOnline ? 'text-emerald-600' : isAttention ? 'text-amber-600' : 'text-gray-400'
                }`}>person</span>
              )}
            </div>
            {/* Pulsing status dot */}
            <span className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-[2.5px] border-white ${
              isOnline ? 'bg-emerald-500' : isAttention ? 'bg-amber-500 animate-pulse' : 'bg-gray-400'
            }`} />
          </div>

          {/* Name + Device */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <h4 className="text-lg font-bold text-gray-900 truncate">{name}</h4>
              {onEditSenior && (
                <button
                  onClick={onEditSenior}
                  className="shrink-0 p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-brand-primary transition-colors"
                  aria-label="Edit senior details"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>edit</span>
                </button>
              )}
            </div>
            <p className="text-sm text-gray-500 truncate mt-0.5">{deviceName}</p>
          </div>

          {/* Status Badge */}
          <div className="shrink-0">
            {isOnline && (
              <Badge variant="success" dot pulse>
                Online
              </Badge>
            )}
            {isOffline && (
              <Badge variant="default" dot>
                Offline
              </Badge>
            )}
            {isAttention && (
              <Badge variant="warning">
                <span className="material-symbols-outlined text-xs">warning</span>
                Attention
              </Badge>
            )}
          </div>
        </div>

        {/* Subscription Badge */}
        {subscriptionStatus && (
          <div className="mb-3">
            <div className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 ${
              subscriptionStatus === 'cancelled' || subscriptionStatus === 'expired'
                ? 'bg-gray-100 text-gray-500'
                : hasPayment
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-blue-50 text-blue-700'
            }`}>
              <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>
                {subscriptionStatus === 'cancelled' || subscriptionStatus === 'expired' ? 'cancel'
                  : hasPayment ? 'verified'
                  : 'info'}
              </span>
              {subscriptionStatus === 'cancelled' && 'Subscription Cancelled'}
              {subscriptionStatus === 'expired' && 'Subscription Expired'}
              {subscriptionStatus === 'trial' && !hasPayment && 'Payment not set up'}
              {hasPayment && trialDaysLeft !== null && trialDaysLeft > 0 && (
                `Trial ends ${formatTrialEndDate()} · CA$10.99/month after`
              )}
              {hasPayment && (trialDaysLeft === null || trialDaysLeft === 0) && subscriptionStatus !== 'cancelled' && subscriptionStatus !== 'expired' && (
                'Protection Plan — Active'
              )}
            </div>
            {hasPayment && stripeCustomerId && (
              <button
                onClick={handleManageBilling}
                className="mt-1 text-xs text-brand-primary font-medium bg-transparent border-none cursor-pointer p-0 hover:underline"
              >
                Manage billing &rarr;
              </button>
            )}
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          {/* Profile */}
          <div className="flex flex-col items-center text-center p-2.5 rounded-xl bg-gray-50">
            <span className="material-symbols-outlined text-brand-primary mb-1" style={{ fontSize: '20px' }}>
              {getProfileIcon(profileType)}
            </span>
            <span className="text-[11px] uppercase text-gray-400 font-semibold tracking-wider leading-tight">Profile</span>
            <span className="text-xs font-bold text-gray-700 mt-0.5 line-clamp-1">{getProfileDisplayName(profileType)}</span>
          </div>

          {/* Battery */}
          <div className="flex flex-col items-center text-center p-2.5 rounded-xl bg-gray-50">
            <span className={`material-symbols-outlined mb-1 ${batteryColor}`} style={{ fontSize: '20px' }}>
              {batteryIcon}
            </span>
            <span className="text-[11px] uppercase text-gray-400 font-semibold tracking-wider leading-tight">Battery</span>
            {battery !== undefined ? (
              <div className="flex items-center gap-1 mt-0.5">
                <span className={`text-xs font-bold ${batteryColor}`}>{battery}%</span>
              </div>
            ) : (
              <span className="text-xs font-bold text-gray-400 mt-0.5">—</span>
            )}
          </div>

          {/* Last Seen / Uptime */}
          <div className="flex flex-col items-center text-center p-2.5 rounded-xl bg-gray-50">
            <span className={`material-symbols-outlined mb-1 ${isOnline ? 'text-emerald-500' : 'text-gray-400'}`} style={{ fontSize: '20px' }}>
              {isOnline ? 'wifi' : 'schedule'}
            </span>
            <span className="text-[11px] uppercase text-gray-400 font-semibold tracking-wider leading-tight">
              {isOnline ? 'Status' : 'Last Seen'}
            </span>
            <span className={`text-xs font-bold mt-0.5 ${isOnline ? 'text-emerald-600' : 'text-gray-500'}`}>
              {isOnline ? 'Active' : (lastSeen || '—')}
            </span>
          </div>
        </div>

        {/* Activation Code */}
        {activationCode && (
          <button
            onClick={onViewCode}
            className="flex items-center gap-2.5 mb-4 px-3 py-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors group/code"
          >
            <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '18px' }}>qr_code_2</span>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Code</span>
              <span className="text-sm font-mono font-bold text-gray-800 tracking-wider">{activationCode}</span>
            </div>
            <span className="material-symbols-outlined text-gray-300 group-hover/code:text-brand-primary transition-colors" style={{ fontSize: '16px' }}>open_in_new</span>
          </button>
        )}

        {/* Action Buttons */}
        <div className="pt-4 space-y-2.5">
          {/* Primary Actions */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={onEditConfig}
              className="py-2.5 px-3 text-sm font-semibold text-white bg-brand-primary rounded-xl hover:bg-brand-primary-dark transition-all active:scale-[0.98] flex items-center justify-center gap-1.5 shadow-sm hover:shadow"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>settings</span>
              Configure
            </button>
            <button
              onClick={onEditContacts}
              className="py-2.5 px-3 text-sm font-semibold text-brand-primary bg-brand-primary/8 rounded-xl hover:bg-brand-primary/15 transition-all active:scale-[0.98] flex items-center justify-center gap-1.5"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>contacts</span>
              Contacts
            </button>
          </div>

          {/* Secondary Actions */}
          <div className="flex gap-2">
            <button
              onClick={onViewActivity}
              className="flex-1 py-2 px-3 text-sm font-medium text-gray-500 bg-gray-50 rounded-xl hover:bg-gray-100 hover:text-gray-700 transition-all active:scale-[0.98] flex items-center justify-center gap-1.5"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>timeline</span>
              Activity
            </button>
            {onLocate && (
              <button
                onClick={onLocate}
                className={`py-2 px-3 rounded-xl transition-all active:scale-[0.98] flex items-center justify-center ${
                  hasLocation
                    ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                    : 'text-gray-400 bg-gray-50 hover:bg-gray-100'
                }`}
                aria-label="Locate senior"
                title="Locate"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>location_on</span>
              </button>
            )}
            {/* REMOVED: the geofence button (aria-label "Safe zone settings",
                title "Safe Zone"). Geofencing is CUT from v1 (Feature 03 spec).
                No caller ever passed onGeofence, so it never rendered — but the
                labels still shipped in the bundle, and a screen reader would
                have announced a safe-zone control for a feature that does not
                exist. Restore with the rest of the geofence UI in Wave 1.5. */}
            {onDelete && (
              <button
                onClick={onDelete}
                className="py-2 px-3 text-gray-400 bg-gray-50 rounded-xl hover:bg-red-50 hover:text-red-500 transition-all active:scale-[0.98] flex items-center justify-center"
                aria-label="Delete senior"
                title="Delete"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>delete</span>
              </button>
            )}
          </div>

          {/* Subscribe banner — show only when trial without payment, or expired/cancelled */}
          {onSubscribe && subscriptionStatus && (
            (subscriptionStatus === 'trial' && !hasPayment) ||
            subscriptionStatus === 'expired' ||
            subscriptionStatus === 'cancelled'
          ) && (
            <button
              onClick={onSubscribe}
              className="w-full mt-3 py-3 px-4 bg-gradient-to-r from-brand-accent to-[#c96a3f] text-white rounded-xl font-semibold text-sm hover:shadow-lg hover:shadow-brand-accent/30 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>rocket_launch</span>
              {subscriptionStatus === 'trial' ? 'Start 30-Day Free Trial' : 'Reactivate Subscription'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeniorCard;
